import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { wildcardInvalidateQuery } from "@/lib/utils";
import type { EpisodeOfCareLifeCycleUpdate } from "@/types/EpisodeOfCareLifecycle";
import { useQueryClient } from "@tanstack/react-query";
import type { CarePlan } from "fhir/r4";

type DeleteCarePlanResponse = {
  data: {
    carePlan: CarePlan;
    episodeOfCareLifecycleUpdate?: EpisodeOfCareLifeCycleUpdate;
  };
};

export const useDeleteCarePlan = ({
  patientId,
  motherEncounterId,
  encounterId,
  onError,
}: {
  patientId: string;
  motherEncounterId: string;
  encounterId: string;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<DeleteCarePlanResponse, void>({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.DELETE(patientId),
      mutationFn: () => {
        return axiosInstance.delete(
          `/charting/encounter-charting/${encounterId}/careplan`,
        );
      },
      onSuccess: (response) => {
        if (response.data?.episodeOfCareLifecycleUpdate) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.DEFAULT({
              patientId,
              encounterId: motherEncounterId,
            }),
          });
        }

        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.SUMMARY({
            patientId,
            encounterId,
          }),
        });

        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Timeline.ENCOUNTER({
            patientId,
            motherEncounterId,
            encounterId,
          }),
        });

        if (response.data?.episodeOfCareLifecycleUpdate) {
          queryClient.invalidateQueries({
            queryKey:
              QueryKeys.Patient.MedicalRecords.MedicalHistory.DEFAULT(
                patientId,
              ),
          });
        }

        wildcardInvalidateQuery(
          queryClient,
          QueryKeys.Distribution.AVAILABLE_ACTIONS({
            patientId,
            encounterId: "*",
          }),
        );
      },
      onError: (error) => {
        onError?.(error);
      },
    },
  });
};
